/**
 * Financial Year Utilities
 * Australian FY runs from July 1 to June 30
 * FY2024/25 = July 1, 2024 - June 30, 2025
 */

/**
 * Get the financial year for a given date
 * @param {Date|string} date - The date to check
 * @returns {number} The financial year (e.g., 2025 for FY24/25)
 */
export const getFinancialYear = (date) => {
  const d = new Date(date);
  const month = d.getMonth(); // 0-indexed (0 = Jan, 6 = July)
  const year = d.getFullYear();
  return month >= 6 ? year + 1 : year;
};

/**
 * Get the FY label for a given financial year
 * @param {number} fyYear - The financial year (e.g., 2025)
 * @returns {string} The label (e.g., "FY24/25")
 */
export const getFYLabel = (fyYear) => {
  const startYear = (fyYear - 1).toString().slice(-2);
  const endYear = fyYear.toString().slice(-2);
  return `FY${startYear}/${endYear}`;
};

/**
 * Get FY start and end dates
 * @param {number} fyYear - The financial year (e.g., 2025)
 * @returns {{ start: Date, end: Date }}
 */
export const getFYDateRange = (fyYear) => {
  return {
    start: new Date(fyYear - 1, 6, 1), // July 1 of previous year
    end: new Date(fyYear, 5, 30)        // June 30 of FY year
  };
};

/**
 * Calculate pro-rata FY allocation for an emission spanning multiple FYs
 * @param {Date|string} startDate - Activity start date
 * @param {Date|string|null} endDate - Activity end date (null = point-in-time)
 * @param {number} totalAmount - Total CO2-e for the period
 * @returns {Array<{ fy: string, fyYear: number, amount: number, days: number }>}
 */
export const calculateFYAllocation = (startDate, endDate, totalAmount) => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date(startDate);
  
  // Total days in the period
  const totalDays = Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
  
  const allocations = [];
  let currentFY = getFinancialYear(start);
  let currentDate = new Date(start);
  
  while (currentDate <= end) {
    const fyRange = getFYDateRange(currentFY);
    
    // Calculate days in this FY
    const periodStart = currentDate > fyRange.start ? currentDate : fyRange.start;
    const periodEnd = end < fyRange.end ? end : fyRange.end;
    const daysInFY = Math.max(0, Math.floor((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1);
    
    if (daysInFY > 0) {
      // Pro-rata allocation
      const allocatedAmount = Number(((totalAmount * daysInFY) / totalDays).toFixed(6));
      
      allocations.push({
        fy: getFYLabel(currentFY),
        fyYear: currentFY,
        amount: allocatedAmount,
        days: daysInFY
      });
    }
    
    // Move to next FY
    currentFY++;
    currentDate = new Date(currentFY - 1, 6, 1); // July 1 of next FY
  }
  
  return allocations;
};

/**
 * Get reporting periods string for display
 * @param {Date|string} startDate - Activity start date
 * @param {Date|string|null} endDate - Activity end date
 * @returns {string} Comma-separated FY labels (e.g., "FY24/25, FY25/26")
 */
export const getReportingPeriods = (startDate, endDate) => {
  const allocations = calculateFYAllocation(startDate, endDate, 1); // Amount doesn't matter for labels
  return allocations.map(a => a.fy).join(', ');
};

/**
 * Check if a date range spans multiple FYs
 * @param {Date|string} startDate 
 * @param {Date|string|null} endDate 
 * @returns {boolean}
 */
export const spansMultipleFYs = (startDate, endDate) => {
  if (!endDate) return false;
  return getFinancialYear(startDate) !== getFinancialYear(endDate);
};

/**
 * Format activity date for display
 * @param {Date|string} startDate 
 * @param {Date|string|null} endDate 
 * @returns {string} "DD/MM/YY" or "DD/MM/YY - DD/MM/YY"
 */
export const formatActivityDate = (startDate, endDate) => {
  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };
  
  if (!endDate || new Date(startDate).toDateString() === new Date(endDate).toDateString()) {
    return formatDate(startDate);
  }
  
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Format entry date (audit timestamp) for display
 * @param {Date|string} entryDate 
 * @param {string} timezone - Timezone string (default: 'Australia/Sydney')
 * @returns {string} "DD/MM/YY HH:mm"
 */
export const formatEntryDate = (entryDate, timezone = 'Australia/Sydney') => {
  const date = new Date(entryDate);
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone
  });
};

/**
 * Get available financial years (current + past 5 + future 1)
 * @returns {Array<{ value: number, label: string }>}
 */
export const getAvailableFYs = () => {
  const currentFY = getFinancialYear(new Date());
  const fys = [];
  
  for (let fy = currentFY + 1; fy >= currentFY - 5; fy--) {
    fys.push({
      value: fy,
      label: getFYLabel(fy)
    });
  }
  
  return fys;
};

export default {
  getFinancialYear,
  getFYLabel,
  getFYDateRange,
  calculateFYAllocation,
  getReportingPeriods,
  spansMultipleFYs,
  formatActivityDate,
  formatEntryDate,
  getAvailableFYs
};
